/* ============================================
   HashtagManager — Gestión de grupos de hashtags
   Chips interactivos, Enter/coma para agregar
   ============================================ */
import { useState } from 'react'
import { IconPlus, IconTrash, IconX, IconTag } from '@/components/icons/Icons'
import type { HashtagGroup } from '@/lib/types'

interface HashtagManagerProps {
  groups: HashtagGroup[]
  onChange: (groups: HashtagGroup[]) => void
}

export default function HashtagManager({ groups, onChange }: HashtagManagerProps) {
  const [newTagInputs, setNewTagInputs] = useState<Record<number, string>>({})

  const uniqueTagCount = new Set(groups.flatMap(g => g.tags)).size

  function handleAddGroup(): void {
    onChange([...groups, { name: '', tags: [] }])
  }

  function handleRemoveGroup(index: number): void {
    onChange(groups.filter((_, i) => i !== index))
  }

  function handleGroupNameChange(index: number, name: string): void {
    const updated = groups.map((g, i) => (i === index ? { ...g, name } : g))
    onChange(updated)
  }

  function handleAddTag(groupIndex: number, raw: string): void {
    const tag = raw.trim().replace(/^#*/, '')
    if (!tag) return
    const group = groups[groupIndex]
    if (group.tags.includes(tag)) return
    const updated = groups.map((g, i) =>
      i === groupIndex ? { ...g, tags: [...g.tags, tag] } : g
    )
    onChange(updated)
    setNewTagInputs(prev => ({ ...prev, [groupIndex]: '' }))
  }

  function handleRemoveTag(groupIndex: number, tagIndex: number): void {
    const updated = groups.map((g, i) =>
      i === groupIndex ? { ...g, tags: g.tags.filter((_, ti) => ti !== tagIndex) } : g
    )
    onChange(updated)
  }

  function handleTagInputKeyDown(
    e: React.KeyboardEvent<HTMLInputElement>,
    groupIndex: number
  ): void {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      const value = newTagInputs[groupIndex] || ''
      // Support comma-separated batch entry
      const parts = value.split(',')
      for (const part of parts) {
        handleAddTag(groupIndex, part)
      }
    }
  }

  return (
    <div className="hashtag-manager">
      <div className="hashtag-manager__header">
        <div className="hashtag-manager__title-row">
          <IconTag size={16} />
          <span className="hashtag-manager__count">
            {uniqueTagCount} hashtag{uniqueTagCount !== 1 ? 's' : ''} únicos
          </span>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={handleAddGroup}
        >
          <IconPlus size={14} /> Grupo
        </button>
      </div>

      {groups.length === 0 && (
        <div className="hashtag-manager__empty">
          <p>Sin grupos de hashtags. Agrega uno para organizar tus tags.</p>
        </div>
      )}

      <div className="hashtag-manager__groups">
        {groups.map((group, gi) => (
          <div key={gi} className="hashtag-group-card">
            <div className="hashtag-group-card__header">
              <input
                type="text"
                className="form-input hashtag-group-card__name"
                placeholder="Nombre del grupo..."
                value={group.name}
                onChange={e => handleGroupNameChange(gi, e.target.value)}
              />
              <button
                type="button"
                className="btn btn-icon btn-sm"
                onClick={() => handleRemoveGroup(gi)}
                title="Eliminar grupo"
                style={{ color: 'var(--danger)' }}
              >
                <IconTrash size={14} />
              </button>
            </div>

            <div className="hashtag-group-card__chips">
              {group.tags.map((tag, ti) => (
                <span key={ti} className="hashtag-chip">
                  #{tag}
                  <button
                    type="button"
                    className="hashtag-chip__remove"
                    onClick={() => handleRemoveTag(gi, ti)}
                  >
                    <IconX size={10} />
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="hashtag-group-card__input"
                placeholder="Agregar tag..."
                value={newTagInputs[gi] || ''}
                onChange={e =>
                  setNewTagInputs(prev => ({ ...prev, [gi]: e.target.value }))
                }
                onKeyDown={e => handleTagInputKeyDown(e, gi)}
                onBlur={() => {
                  const val = newTagInputs[gi] || ''
                  if (val.trim()) handleAddTag(gi, val)
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
